import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { loginRedirect } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Contact2, UserPlus, MessageCircle, Search as SearchIcon, Sparkles, Copy } from "lucide-react";

// Contact Picker API is browser-native — best support on Android Chrome/Edge.
// iOS Safari / Desktop Firefox don't expose it → we fall back to a paste-list UX.
const pickerSupported = () =>
  typeof window !== "undefined" && "contacts" in navigator && "ContactsManager" in window;


export default function Contacts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPaste, setShowPaste] = useState(false);

  const load = () => {
    if (!user) return;
    api.get("/contacts").then(({ data }) => setContacts(data)).catch(() => {});
    api.get("/contacts/suggestions").then(({ data }) => setSuggestions(data)).catch(() => {});
  };
  useEffect(load, [user]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!q) return contacts;
    const s = q.toLowerCase();
    return contacts.filter((c) => (c.name || "").toLowerCase().includes(s) || (c.phone || "").includes(s));
  }, [q, contacts]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <h2 className="heading text-3xl font-extrabold mb-3">Please login</h2>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="contacts-login">Login with Google</Button>
      </div>
    );
  }

  const pickFromDevice = async () => {
    if (!pickerSupported()) {
      setShowPaste(true);
      toast.info("Your browser doesn't expose the OS contact picker. Paste contacts instead.");
      return;
    }
    setBusy(true);
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: true });
      const contactsPayload = picked.flatMap((c) => {
        const name = (c.name?.[0] || "").trim() || "(no name)";
        return (c.tel || []).map((t) => ({ name, phone: t }));
      });
      if (!contactsPayload.length) { toast.info("No contacts selected."); return; }
      const { data } = await api.post("/contacts/import", { contacts: contactsPayload });
      toast.success(`Imported ${data.imported} — matched ${data.matched} on Find2Near`);
      load();
    } catch (e) {
      // "SecurityError" typically means the user cancelled the picker.
      if (e?.name !== "AbortError") toast.error(e.message || "Could not read contacts");
    } finally { setBusy(false); }
  };

  const importPasted = async (text) => {
    // Accepts "Name, +91 98xxx" per line, or "Name TAB phone", or just phone.
    const rows = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const parsed = rows.map((line) => {
      const parts = line.split(/[,\t]/);
      if (parts.length >= 2) return { name: parts[0].trim(), phone: parts.slice(1).join(" ").trim() };
      return { name: line.replace(/[\d\s+\-()]/g, "").trim() || "(no name)", phone: line };
    }).filter((r) => r.phone);
    if (!parsed.length) return toast.error("Nothing to import");
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/import", { contacts: parsed });
      toast.success(`Imported ${data.imported} — matched ${data.matched} on Find2Near`);
      setShowPaste(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const invite = async (row, channel) => {
    try {
      await api.post("/contacts/invite", { phone: row.phone, channel });
      const msg = encodeURIComponent(`Hey ${row.name}, join me on Find2Near — https://find2near.com`);
      if (channel === "whatsapp") window.open(`https://wa.me/${row.phone.replace(/\D/g, "")}?text=${msg}`, "_blank");
      else if (channel === "sms") window.location.href = `sms:${row.phone}?body=${msg}`;
      else if (channel === "share" && navigator.share) navigator.share({ text: decodeURIComponent(msg) });
    } catch (e) { toast.error("Invite failed"); }
  };

  const matched = filtered.filter((c) => c.matched_user_id);
  const unmatched = filtered.filter((c) => !c.matched_user_id);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="contacts-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="contacts-back"><ArrowLeft size={16} className="mr-1" /> Back</Button>
        <h1 className="heading text-2xl font-extrabold flex items-center gap-2"><Contact2 className="text-[#E05E36]" /> My Contacts</h1>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <p className="text-sm text-stone-600">
            Import contacts from your phone in one tap. Names auto-link to each phone number, and anyone already on Find2Near gets a match badge.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={pickFromDevice} disabled={busy} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-2" data-testid="contacts-pick-device">
              <Sparkles size={14} /> {busy ? "Reading…" : "Import from device"}
            </Button>
            <Button variant="outline" onClick={() => setShowPaste((s) => !s)} className="rounded-full gap-2" data-testid="contacts-paste-toggle">
              <Copy size={14} /> Paste list
            </Button>
          </div>
          {!pickerSupported() && (
            <div className="text-xs text-stone-500" data-testid="contacts-picker-hint">
              Tip: The one-tap importer works on Android Chrome/Edge. On iOS or desktop, use “Paste list” — one contact per line, format: <code>Name, +91 98xxxxxxxx</code>.
            </div>
          )}
          {showPaste && <PasteBox busy={busy} onImport={importPasted} />}
        </CardContent>
      </Card>

      <div className="relative">
        <SearchIcon size={14} className="absolute left-3 top-3 text-stone-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts…" className="pl-9" data-testid="contacts-search" />
      </div>

      {suggestions.length > 0 && (
        <Card data-testid="friend-suggestions">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles size={16} className="text-[#E05E36]" /> People you may know
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {suggestions.map((s) => (
                <div key={s.user_id} className="min-w-[150px] max-w-[150px] shrink-0 rounded-xl border border-stone-200 p-3 text-center bg-white" data-testid={`suggestion-${s.user_id}`}>
                  <div className="w-12 h-12 mx-auto rounded-full bg-stone-100 overflow-hidden flex items-center justify-center text-stone-500 font-bold">
                    {s.picture ? <img loading="lazy" src={s.picture} alt="" className="w-full h-full object-cover" /> : (s.name?.[0]?.toUpperCase() || "?")}
                  </div>
                  <div className="mt-2 font-semibold text-sm truncate">{s.name}</div>
                  <div className="text-[10px] text-stone-500 truncate">{s.occupation || s.city || s.contact_name}</div>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/u/${s.user_id}`)} className="mt-2 rounded-full w-full text-xs" data-testid={`suggestion-open-${s.user_id}`}>
                    Open
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {matched.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">On Find2Near ({matched.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {matched.map((c) => (
              <Row key={`m-${c.phone}`} c={c} isMatched onInvite={invite} />
            ))}
          </CardContent>
        </Card>
      )}

      {unmatched.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Not yet on Find2Near ({unmatched.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {unmatched.map((c) => (
              <Row key={`u-${c.phone}`} c={c} onInvite={invite} />
            ))}
          </CardContent>
        </Card>
      )}

      {contacts.length === 0 && (
        <div className="text-center py-16 text-stone-500" data-testid="contacts-empty">
          No contacts imported yet. Tap “Import from device” to start.
        </div>
      )}
    </div>
  );
}


function Row({ c, isMatched, onInvite }) {
  const nav = useNavigate();
  const goProfile = () => c.match?.user_id && nav(`/u/${c.match.user_id}`);
  return (
    <div className="flex items-center gap-3 py-2 border-b border-stone-100 last:border-0" data-testid={`contact-row-${c.phone}`}>
      <div className="w-10 h-10 rounded-full bg-stone-100 overflow-hidden flex items-center justify-center text-stone-500 font-bold">
        {c.match?.picture
          ? <img loading="lazy" src={c.match.picture} alt="" className="w-full h-full object-cover" />
          : (c.name?.[0]?.toUpperCase() || "?")}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold truncate">{c.match?.name || c.name}</span>
          {isMatched && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="contact-badge-matched">On Find2Near</Badge>}
          {c.match?.wuid && <Badge variant="outline" className="text-[10px] font-mono">WUID {c.match.wuid}</Badge>}
        </div>
        <div className="text-xs text-stone-500">{c.phone}</div>
      </div>
      {isMatched ? (
        <Button size="sm" variant="outline" onClick={goProfile} className="rounded-full gap-2" data-testid="contact-open-profile">
          <MessageCircle size={14} /> Open
        </Button>
      ) : (
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => onInvite(c, "whatsapp")} className="rounded-full text-xs" data-testid="contact-invite-wa">WhatsApp</Button>
          <Button size="sm" variant="ghost" onClick={() => onInvite(c, "sms")} className="rounded-full text-xs gap-1" data-testid="contact-invite-sms"><UserPlus size={12} /> SMS</Button>
        </div>
      )}
    </div>
  );
}


function PasteBox({ busy, onImport }) {
  const [text, setText] = useState("");
  return (
    <div className="space-y-2">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Rita, +91 98765 43210&#10;Aman, 9123456789&#10;+44 7911 123456"
        rows={5}
        className="w-full border border-stone-300 rounded-lg p-3 text-sm font-mono"
        data-testid="contacts-paste-box"
      />
      <Button disabled={busy || !text.trim()} onClick={() => onImport(text)} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="contacts-paste-import">
        {busy ? "Importing…" : "Import pasted list"}
      </Button>
    </div>
  );
}
